import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MessageSquare, Star, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import client from '@/services/client';

const TABS = [
  { key: '',            label: 'All',         count: (b) => (b.org_preview || 0) + (b.live || 0) },
  { key: 'org_preview', label: '24h Preview', count: (b) => b.org_preview || 0 },
  { key: 'live',        label: 'Live',        count: (b) => b.live || 0 },
];

function StarsReadonly({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="review-stars-readonly">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-zinc-700'}`} />
      ))}
    </span>
  );
}

export default function RecruiterReviews() {
  const [data, setData] = useState({ reviews: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const q = tab ? `?status=${tab}` : '';
      const res = await client.get(`/recruiter/reviews${q}`);
      setData({ reviews: res.data?.reviews || [], total: res.data?.total || 0 });
    } catch {
      toast.error('Could not load reviews');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load();}, [tab]);

  const buckets = useMemo(() => {
    const b = { org_preview: 0, live: 0 };
    for (const r of data.reviews) {
      if (r.moderation_status && b[r.moderation_status] !== undefined) b[r.moderation_status] += 1;
    }
    return b;
  }, [data.reviews]);

  return (
    <div data-testid="recruiter-reviews">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <MessageSquare className="w-6 h-6" /> Candidate Reviews
          </h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm max-w-2xl">
            Reviews from candidates you&apos;ve hired or turned down. Reviews sit in a 24-hour preview so you can respond internally before they go live.
            Admins can revoke reviews at any time; you cannot delete them yourself.
          </p>
        </div>
        <button onClick={load} className="text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1" data-testid="recruiter-reviews-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-zinc-900 rounded-full p-1 mb-4 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key || 'all'}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition ${tab === t.key ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-zinc-500'}`}
            data-testid={`recruiter-reviews-tab-${t.key || 'all'}`}
          >
            {t.label} {t.count(buckets) > 0 && <span className="text-slate-400 dark:text-zinc-500 ml-1">({t.count(buckets)})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="p-8 text-center text-slate-400 text-sm">Loading…</p>
      ) : data.reviews.length === 0 ? (
        <div className="p-10 text-center bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg">
          <p className="text-slate-500 dark:text-zinc-400 text-sm">No reviews yet.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {data.reviews.map((r) => (
            <li key={r.id} className="p-4 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg" data-testid={`recruiter-review-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StarsReadonly value={r.rating} />
                    {r.moderation_status === 'org_preview' ? (
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400">
                        <Clock className="w-3 h-3" /> Preview · goes live {new Date(r.publish_at).toLocaleString()}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle className="w-3 h-3" /> Live
                      </span>
                    )}
                  </div>
                  {r.title && <p className="font-medium text-slate-900 dark:text-white">{r.title}</p>}
                  <p className="text-[13.5px] text-slate-700 dark:text-zinc-300 mt-1 whitespace-pre-wrap">{r.body}</p>
                  <p className="text-[11.5px] text-slate-400 dark:text-zinc-500 mt-2">
                    {r.job_title || 'Role'} · {r.applicant_name || 'Candidate'} · {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
